import AdminPermission from "components/admin/AdminPermission";
import useAdminAccess from "hooks/useAdminAccess";
import { httpGetRate } from "api/admin/stats";
import {
  httpFetchPlanTransactions,
  httpSwapStatus,
} from "api/admin/transaction";
import { Itransaction } from "components/ui/Transaction";
import TransactionItem from "components/ui/TransactionItem";
import TransactionDevice from "components/admin/devices/TransactionDevice";
import VButton from "components/ui/VButton";
import { DateTime } from "luxon";
import { useState } from "react";
import ReactModal from "react-modal";
import { useQuery } from "react-query";
import currency, { cryptoFormat, moneyFormat } from "utilities/currency";
import {
  capitalize,
  frequencyTime,
  showDanger,
  showSuccess,
} from "utilities/misc";
import { post } from "utilities/network";
import {
  parseVirtualCardMetadata,
  virtualCardTransactionTitle,
} from "utilities/virtualCardTransactions";
import {
  additionalCryptos,
  cryptos,
  refactoredCryptoKey,
} from "utilities/wallets";
import { billsTitle } from ".";

const bitoshiCryptos = [...cryptos, ...additionalCryptos];

function safeJsonParse<T>(raw: string | null | undefined, fallback: T): T {
  if (raw == null || raw === "") return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export const isRiskDispatchPending = (transaction: Itransaction) =>
  transaction.status === "pending" &&
  safeJsonParse(transaction.rate, {} as Record<string, unknown>)
    .riskApprovalDispatch === "pending";

const depositTransactionStatus = (transaction: Itransaction) => {
  let status: string = transaction.status;

  if (transaction.status === "pending") {
    if (transaction.paid) {
      status = "Paid";
    } else {
      status = "Unpaid";
    }
  }
  return status;
};

const TransactionInfo: React.FC<{
  transaction: Itransaction | null;
  refetch?: () => void;
  full?: boolean;
}> = ({ transaction, refetch, full = false }) => {
  if (!transaction) return <div />;

  switch (transaction.transactionType) {
    case "deposit":
      return (
        <Deposit transaction={transaction} refetch={refetch} full={full} />
      );
    case "withdrawal":
      return (
        <Withdrawal transaction={transaction} refetch={refetch} full={full} />
      );
    case "refund":
      return <Refund transaction={transaction} refetch={refetch} full={full} />;
    case "earning":
      return <Earning transaction={transaction} full={full} />;
    case "swap":
      return <Swap transaction={transaction} refetch={refetch} full={full} />;
    case "send":
      return <Send transaction={transaction} refetch={refetch} full={full} />;
    case "receive":
      return (
        <Receive transaction={transaction} refetch={refetch} full={full} />
      );
    case "buy":
      return <Buy transaction={transaction} refetch={refetch} full={full} />;
    case "sell":
      return <Sell transaction={transaction} refetch={refetch} full={full} />;
    case "bill":
      return <Bill transaction={transaction} refetch={refetch} full={full} />;
    case "virtual-card":
      return <VirtualCard transaction={transaction} full={full} />;
    default:
      return transaction.frequency ? (
        <Recurring transaction={transaction} full={full} />
      ) : (
        <Purchase transaction={transaction} full={full} />
      );
  }
};

export const Deposit = ({
  transaction,
  refetch,
  full,
}: {
  transaction: Itransaction;
  refetch?: () => void;
  full?: boolean;
}) => {
  let bank =
    transaction?.address && transaction?.address !== "sell.bitoshi.africa"
      ? safeJsonParse(transaction.address, {} as Record<string, any>)
      : {};

  const [confirmModal, setConfirmModal] = useState(false);

  const close = () => {
    setConfirmModal(false);
  };

  const handleSubmit = () => {
    post(`admin/transactions/paymentReceived/${transaction.id}`, {})
      .then(({ data }) => {
        if (data.success) {
          if (refetch) {
            refetch();
          }
          showSuccess("Payment marked as successful");
          setConfirmModal(false);
        }
      })
      .catch((e) => {
        showDanger("Something went wrong");
        setConfirmModal(false);
      });
  };

  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ReactModal
        shouldCloseOnOverlayClick={true}
        onRequestClose={close}
        isOpen={confirmModal}
        className="w-screen h-screen"
      >
        <div
          onClick={close}
          className="relative w-full h-full bg-gray-500 bg-opacity-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-0 flex flex-col items-center justify-center w-full px-5 bg-white rounded-t-lg py-28"
          >
            <div className="absolute w-20 h-1 bg-white rounded-lg -top-5"></div>
            <h4 className="mb-5 text-xl">
              Payment{" "}
              {transaction.crypto === "reversal" ? "Reversed" : "Received"}?
            </h4>

            <div className="flex mt-10 text-center">
              <AdminPermission
                permission={"transactions.paymentReconciliation.approve"}
              >
                <VButton
                  onClick={handleSubmit}
                  className="mr-20 text-white bg-green-500"
                >
                  Yes
                </VButton>
              </AdminPermission>
              <AdminPermission
                permission={"transactions.paymentReconciliation.approve"}
              >
                <VButton
                  onClick={() => setConfirmModal((p) => !p)}
                  className="text-white"
                >
                  No
                </VButton>
              </AdminPermission>
            </div>
          </div>
        </div>
      </ReactModal>
      <h4 className="mb-5">Deposited NGN</h4>
      <TransactionDevice device={transaction.device} />

      <TransactionItem
        title="Amount"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(
          Number(transaction.amount) - Number(transaction.fee),
        )}`}
        isAmount
      />
      <TransactionItem
        title="Fee"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.fee)}`}
      />
      <TransactionItem
        title="Reference ID"
        value={transaction?.user?.userName}
      />
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />
      <TransactionItem title="Bank name" value={bank?.bankName} />
      <TransactionItem title="Account Number" value={bank?.accountNumber} />
      <TransactionItem title="Account name" value={bank?.accountName} />
      {/* <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />
    */}
      <TransactionItem
        title={
          transaction?.paymentProviderName ? "Payment provider" : "Merchant"
        }
        underline
        value={
          transaction?.paymentProviderName || `@${transaction?.merchant || ""}`
        }
      />
      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />
      <TransactionItem
        title="Status"
        isStatus
        value={depositTransactionStatus(transaction)}
        status={
          transaction.status === "pending"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />

      {transaction.status === "pending" && transaction.crypto === "reversal" ? (
        <div className="mt-10 text-center">
          <AdminPermission
            permission={"transactions.paymentReconciliation.approve"}
          >
            <VButton
              onClick={() => setConfirmModal((p) => !p)}
              className="text-white"
            >
              Payment Reversed
            </VButton>
          </AdminPermission>
        </div>
      ) : null}

      {transaction.status === "cancelled" && (
        <div className="mt-10 text-center">
          <AdminPermission
            permission={"transactions.paymentReconciliation.approve"}
          >
            <VButton
              onClick={() => setConfirmModal((p) => !p)}
              className="text-white"
            >
              Payment Received
            </VButton>
          </AdminPermission>
        </div>
      )}
    </div>
  );
};

export const Withdrawal = ({
  transaction,
  full,
  refetch,
}: {
  transaction: Itransaction;
  full?: boolean;
  refetch?: () => void;
}) => {
  let bank =
    transaction?.address && transaction?.address !== "sell.bitoshi.africa"
      ? safeJsonParse(transaction.address, {} as Record<string, any>)
      : {};

  const isCrypto = transaction.currencyType === "crypto";

  const [confirmModal, setConfirmModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);

  const close = () => {
    setConfirmModal(false);
    setCancelModal(false);
  };

  const isRiskReview =
    transaction.status === "pending_manual_review" ||
    isRiskDispatchPending(transaction);
  const approveEndpoint = isRiskReview
    ? `admin/transactions/riskReview/${transaction.id}/approve`
    : `admin/transactions/paymentSent/${transaction.id}`;
  const rejectEndpoint = isRiskReview
    ? `admin/transactions/riskReview/${transaction.id}/reject`
    : `admin/transactions/paymentFailed/${transaction.id}`;

  const handleSubmit = () => {
    post(approveEndpoint, {})
      .then(({ data }) => {
        if (data.success) {
          if (refetch) {
            refetch();
          }
          showSuccess(
            isRiskReview
              ? "Risk review approved"
              : "Payment marked as successful",
          );
          setConfirmModal(false);
        }
      })
      .catch((e) => {
        showDanger("Something went wrong");
        setConfirmModal(false);
      });
  };

  const handleSubmitForFailed = () => {
    post(rejectEndpoint, {})
      .then(({ data }) => {
        if (data.success) {
          if (refetch) {
            refetch();
          }
          showSuccess(
            isRiskReview
              ? "Risk review rejected"
              : "Payment cancelled successfully",
          );
          setCancelModal(false);
        }
      })
      .catch((e) => {
        showDanger("Something went wrong");
        setCancelModal(false);
      });
  };

  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ReactModal
        shouldCloseOnOverlayClick={true}
        onRequestClose={close}
        isOpen={confirmModal || cancelModal}
        className="w-screen h-screen"
      >
        <div
          onClick={close}
          className="relative w-full h-full bg-gray-500 bg-opacity-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-0 flex flex-col items-center justify-center w-full px-5 bg-white rounded-t-lg py-28"
          >
            <div className="absolute w-20 h-1 bg-white rounded-lg -top-5"></div>
            <h4 className="mb-5 text-xl">
              {isRiskReview
                ? confirmModal
                  ? "Approve"
                  : "Reject"
                : "Payment " + (confirmModal ? "completed" : "failed")}
              ?
            </h4>

            <div className="flex mt-10 text-center">
              <AdminPermission
                permission={
                  isRiskReview
                    ? "transactions.riskReview.approve"
                    : "transactions.paymentReconciliation.approve"
                }
              >
                <VButton
                  onClick={confirmModal ? handleSubmit : handleSubmitForFailed}
                  className="mr-20 text-white bg-green-500"
                >
                  Yes
                </VButton>
              </AdminPermission>
              <AdminPermission
                permission={
                  isRiskReview
                    ? "transactions.riskReview.approve"
                    : "transactions.paymentReconciliation.approve"
                }
              >
                <VButton
                  onClick={
                    confirmModal
                      ? () => setConfirmModal(false)
                      : () => setCancelModal(false)
                  }
                  className="text-white"
                >
                  No
                </VButton>
              </AdminPermission>
            </div>
          </div>
        </div>
      </ReactModal>
      {transaction.place === "external" ? (
        <span className="px-1 text-xs bg-orange-300 rounded">
          sell.bitoshi.africa
        </span>
      ) : (
        ""
      )}
      <h4 className="mb-5">Sent Money</h4>
      <TransactionDevice device={transaction.device} />
      <TransactionItem
        title="Amount"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(
          Number(transaction.amount) - Number(transaction.fee),
        )}`}
        isAmount
        sub={
          isCrypto ? (
            <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
              <span className="mr-4">
                {`${cryptoFormat(
                  Number(transaction.amount) /
                    bank.usd /
                    bank[transaction?.fromCrypto],
                )} ${transaction?.fromCrypto?.toUpperCase()}`}
              </span>
              <span>
                {`$${moneyFormat(Number(transaction.amount) / bank.usd)}
              `}
              </span>
            </p>
          ) : undefined
        }
      />
      <TransactionItem
        title="Fee"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.fee)}`}
      />
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />
      <TransactionItem
        title="Session ID"
        value={bank?.sessionId || transaction?.transactionHash || ""}
      />
      <TransactionItem title="Bank name" value={bank?.bankName} />
      <TransactionItem title="Account Number" value={bank?.accountNumber} />
      <TransactionItem title="Account name" value={bank?.accountName} />
      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />
      <TransactionItem
        title={
          transaction?.paymentProviderName ? "Payment provider" : "Merchant"
        }
        underline
        value={
          transaction?.paymentProviderName || `@${transaction?.merchant || ""}`
        }
      />
      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />

      <TransactionItem
        title="Source"
        value={
          isCrypto
            ? transaction?.fromCrypto?.toUpperCase()
            : capitalize(transaction?.currencyType)
        }
      />

      {isCrypto && (
        <TransactionItem
          title="Rate"
          value={`1${transaction?.fromCrypto?.toUpperCase()} = $${
            bank[transaction?.fromCrypto]
          }`}
          sub={
            <p className="text-sm font-light text-black text-opacity-70">
              $1 = {bank?.usd}
            </p>
          }
        />
      )}

      {transaction.riskFlagReason && (
        <TransactionItem
          title="Risk Flag Reason"
          value={transaction.riskFlagReason}
        />
      )}
      <TransactionItem
        title="Status"
        isStatus
        value={
          transaction.status === "pending_manual_review"
            ? "Pending Manual Review"
            : transaction.status
        }
        status={
          transaction.status === "pending" ||
          transaction.status === "pending_manual_review"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
      {isRiskDispatchPending(transaction) && (
        <RiskReviewActions transaction={transaction} refetch={refetch} />
      )}
      {!isRiskDispatchPending(transaction) &&
        (transaction.status === "pending" ||
          transaction.status === "pending_manual_review") && (
          <div className="flex items-center justify-center mt-10 gap-x-5 gap-y-5">
            <AdminPermission
              permission={
                isRiskReview
                  ? "transactions.riskReview.approve"
                  : "transactions.paymentReconciliation.approve"
              }
            >
              <VButton
                onClick={() => setConfirmModal((p) => !p)}
                className="text-white bg-green-600"
              >
                {transaction.status === "pending_manual_review"
                  ? "Approve"
                  : "Payment Sent"}
              </VButton>
            </AdminPermission>
            <AdminPermission
              permission={
                isRiskReview
                  ? "transactions.riskReview.approve"
                  : "transactions.paymentReconciliation.approve"
              }
            >
              <VButton
                onClick={() => setCancelModal((p) => !p)}
                className="text-white"
              >
                {transaction.status === "pending_manual_review"
                  ? "Reject"
                  : "Payment Cancelled"}
              </VButton>
            </AdminPermission>
          </div>
        )}
    </div>
  );
};

export const Earning = ({
  transaction,
  full,
}: {
  transaction: Itransaction;
  full?: boolean;
}) => {
  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {transaction.address ? (
        <span className="px-1 text-xs bg-orange-300 rounded">
          {transaction.address}
        </span>
      ) : (
        ""
      )}
      <h4 className="mb-5">Earned NGN</h4>
      <TransactionDevice device={transaction.device} />

      <TransactionItem
        title="Amount"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.amount)}`}
        isAmount
      />

      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />

      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />

      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />
      <TransactionItem
        title="Status"
        isStatus
        value={transaction.status}
        status={
          transaction.status === "pending"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
    </div>
  );
};

export const VirtualCard = ({
  transaction,
  full,
}: {
  transaction: Itransaction;
  full?: boolean;
}) => {
  const metadata = parseVirtualCardMetadata(transaction);

  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h4 className="mb-5">{virtualCardTransactionTitle(transaction)}</h4>

      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      <TransactionItem title="Status" value={capitalize(transaction.status)} />
      <TransactionItem
        title="Amount"
        value={
          transaction.currencyType === "crypto"
            ? `${cryptoFormat(transaction.amount || 0)} ${transaction.crypto?.toUpperCase() || ""}`
            : `${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.amount)}`
        }
      />
      <TransactionItem title="Fee" value={transaction.fee || "0"} />
      {metadata?.fundingMethod && (
        <TransactionItem
          title="Funding method"
          value={metadata.fundingMethod}
        />
      )}
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />
      <TransactionItem
        title="Date"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />
    </div>
  );
};

export const Purchase = ({
  transaction,
  full,
}: {
  transaction: Itransaction;
  full?: boolean;
}) => {
  const rate = safeJsonParse(transaction.rate, {} as Record<string, any>);
  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h4 className="mb-5">Purchases</h4>
      <TransactionDevice device={transaction.device} />

      <TransactionItem title="Asset" value={transaction.crypto.toUpperCase()} />

      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />

      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />

      <TransactionItem
        title="Amount purchased"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.amount)}`}
        sub={
          <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
            {cryptoFormat(
              Number(transaction.amount) /
                rate?.buy?.usd /
                rate?.[transaction.crypto],
            )}{" "}
            {" " + transaction.crypto.toUpperCase()}
          </p>
        }
      />

      <TransactionItem
        title="Name of plan"
        value={transaction?.recurring?.name}
      />

      <TransactionItem
        title="Purchase Date"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />
      <TransactionItem
        title="Purchase amount/frequency"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(
          transaction.amount,
        )}/${capitalize(transaction?.recurring?.frequency || "")}`}
      />
    </div>
  );
};

export const Recurring = ({
  transaction,
  full,
}: {
  transaction: Itransaction;
  full?: boolean;
}) => {
  const { can } = useAdminAccess();
  const endDate = DateTime.fromISO(transaction.timestamp).plus({
    [frequencyTime[transaction.frequency]]:
      transaction.targetAmount / Number(transaction.amount) - 1,
  });

  const {
    data: { transactions = [] },
  } = useQuery(
    ["planTransactions", transaction.id, transaction.crypto],
    () => httpFetchPlanTransactions(transaction.id, transaction.crypto),
    { enabled: can("transactions.search.view"), initialData: [] },
  );
  const {
    data: { prices, rate },
  } = useQuery("rate", httpGetRate, {
    enabled: can("transactions.rates.view"),
    initialData: { prices: [], rate: {} },
  });

  const coin = prices?.find(
    (price: any) => price.symbol === transaction.crypto,
  );

  const purchases =
    transactions?.filter((t: any) => t.type === "purchased") || [];

  const calculatedAverage =
    purchases.reduce((acc: any, t: any) => {
      const rate = safeJsonParse(t.rate, {} as Record<string, any>);
      return acc + Number(rate[t.crypto]);
    }, 0) / purchases.length;

  const costOfPurchases = purchases.reduce((acc: any, t: any) => {
    return acc + Number(t.amount);
  }, 0);

  const amountPurchased = purchases.reduce((acc: any, t: any) => {
    const rate = safeJsonParse(t.rate, {} as Record<string, any>);
    return (
      acc + Number(t.amount) / Number(rate?.buy?.usd) / Number(rate[t.crypto])
    );
  }, 0);

  const amountPurchasedNaira =
    amountPurchased * rate?.buy?.usd * coin?.current_price || 0;
  const calculatedEarnings = () => {
    return amountPurchasedNaira - costOfPurchases;
  };

  const calculatedBalance = amountPurchasedNaira + calculatedEarnings();

  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h4 className="mb-5">{capitalize(transaction.status)} Plan</h4>
      <TransactionDevice device={transaction.device} />

      <TransactionItem title="Asset" value={transaction.crypto.toUpperCase()} />

      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />

      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />

      <TransactionItem
        title="Amount purchased"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.amountPurchased)}`}
        // sub={
        //   <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
        //     {cryptoFormat(
        //       Number(transaction.amount) /
        //         rate?.buy?.usd /
        //         rate?.[transaction.crypto]
        //     )}{" "}
        //     {" " + transaction.crypto.toUpperCase()}
        //   </p>
        // }
      />

      <TransactionItem
        title="Target amount"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.targetAmount)}`}
      />

      <TransactionItem
        title="Start date"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />

      <TransactionItem
        title="End date"
        value={`${endDate.toFormat("DD")} ${endDate.toFormat("t")}`}
      />

      <TransactionItem
        title="Purchase amount/frequency"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(
          transaction.amount,
        )}/${capitalize(transaction?.frequency || "")}`}
      />
      <TransactionItem
        title={`${capitalize(frequencyTime[transaction.frequency])}s left`}
        value={
          (transaction.targetAmount - transaction.amountPurchased) /
          Number(transaction.amount)
        }
      />

      <TransactionItem
        title="Next purchase"
        value={
          transaction.nextSchedule
            ? DateTime.fromISO(transaction.nextSchedule).toFormat("DD")
            : ""
        }
      />
      <TransactionItem title="Name of plan" value={transaction.name} />

      <AdminPermission permission={"transactions.rates.view"}>
        <TransactionItem
          title="Average price"
          value={`${currency("ngn")} ${moneyFormat(calculatedAverage * rate?.buy?.usd)}`}
        />
      </AdminPermission>

      <AdminPermission permission={"transactions.rates.view"}>
        <TransactionItem
          title="Balance"
          value={`${cryptoFormat(
            calculatedBalance / rate?.buy?.usd / coin?.current_price,
          )} ${transaction?.crypto?.toUpperCase()} / ${currency(
            "ngn",
          )} ${moneyFormat(calculatedBalance)} / ${currency("usd")}${moneyFormat(
            calculatedBalance / rate?.buy?.usd,
          )}`}
        />
      </AdminPermission>

      <AdminPermission permission={"transactions.rates.view"}>
        <TransactionItem
          title="Earnings"
          value={`${currency("ngn")} ${moneyFormat(calculatedEarnings())}`}
        />
      </AdminPermission>
    </div>
  );
};

export const Swap = ({
  transaction,
  full,
  refetch,
}: {
  transaction: Itransaction;
  full?: boolean;
  refetch?: () => void;
}) => {
  const { can } = useAdminAccess();
  const fromIsBitoshi = bitoshiCryptos.includes(transaction.fromCrypto);
  const toIsBitoshi = bitoshiCryptos.includes(transaction.crypto);
  const isFullyBitoshi = fromIsBitoshi && toIsBitoshi;
  const newFrom = refactoredCryptoKey(transaction.fromCrypto);
  const newTo = refactoredCryptoKey(transaction.crypto);

  const {
    data: { data: { status = "Pending" } = { status: "Pending" } },
  } = useQuery(
    ["swap-status", transaction.transactionID],
    () => httpSwapStatus({ id: transaction?.transactionHash as string }),
    {
      initialData: { data: { status: "Pending" } },
      enabled:
        !isFullyBitoshi &&
        transaction.status === "pending" &&
        can("transactions.swap.view"),
    },
  );

  const info = safeJsonParse(transaction.rate, {} as Record<string, any>);

  const swapTitle = () => {
    if (transaction.place === "bitoshi" || isFullyBitoshi) {
      return "Bitoshi Internal Swap";
    } else {
      if (fromIsBitoshi && !toIsBitoshi) {
        return "Bitoshi to External Swap";
      } else if (!fromIsBitoshi && toIsBitoshi) {
        return "External to Bitoshi Swap";
      } else {
        return "External Wallet Swap";
      }
    }
  };

  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h4 className="mb-5">{swapTitle()}</h4>
      <TransactionDevice device={transaction.device} />

      <TransactionItem
        title={`Pay`}
        value={`${cryptoFormat(
          transaction.fromAmount,
        )} ${transaction.fromCrypto.toUpperCase()} ${
          fromIsBitoshi
            ? `($${cryptoFormat(
                Number(transaction.fromAmount) * Number(info?.[newFrom] || 0),
              )})`
            : ""
        }`}
      />

      <TransactionItem
        title={`Receive`}
        value={`${cryptoFormat(
          transaction.amount,
        )} ${transaction.crypto.toUpperCase()} ${
          toIsBitoshi
            ? `($${cryptoFormat(
                Number(transaction.amount) * Number(info?.[newTo] || 0),
              )})`
            : ""
        }`}
      />

      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />

      {!toIsBitoshi && (
        <TransactionItem
          title="Input Transaction Hash"
          value={info?.payinHash || ""}
        />
      )}

      {!toIsBitoshi && (
        <TransactionItem
          title="Input Address"
          value={info?.payinAddress || ""}
        />
      )}

      {!isFullyBitoshi && (
        <TransactionItem
          title="Receiving Address"
          value={transaction.address}
        />
      )}

      {!toIsBitoshi && (
        <TransactionItem
          title="Output Transaction Hash"
          value={info?.payoutHash || ""}
        />
      )}

      <TransactionItem
        title="Provider"
        value={isFullyBitoshi ? "Bitoshi" : "Changelly"}
      />

      <TransactionItem
        title="Rate"
        value={`
           1 ${String(
             transaction?.fromCrypto || "",
           ).toUpperCase()} = ${cryptoFormat(
             isFullyBitoshi
               ? info[transaction?.fromCrypto] / info[transaction?.crypto]
               : info?.rate || 0,
           )} ${String(transaction?.crypto).toUpperCase()}
          `}
      />

      <TransactionItem
        title="Swap Fee"
        value={
          isFullyBitoshi
            ? `${cryptoFormat(
                transaction.fee,
              )} ${transaction.fromCrypto.toUpperCase()}`
            : `${cryptoFormat(
                Number(transaction.fee),
              )} ${transaction?.crypto?.toUpperCase()}`
        }
      />

      {!isFullyBitoshi && (
        <TransactionItem
          title="Network Fee"
          value={`${cryptoFormat(
            Number(info.networkFee),
          )} ${transaction?.crypto?.toUpperCase()}`}
        />
      )}

      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />

      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />

      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />

      {transaction.riskFlagReason && (
        <TransactionItem
          title="Risk Flag Reason"
          value={transaction.riskFlagReason}
        />
      )}
      <TransactionItem
        title="Status"
        isStatus
        value={capitalize(
          (fromIsBitoshi && toIsBitoshi) ||
            (transaction?.status !== "pending" &&
              transaction?.status !== "pending_manual_review")
            ? transaction.status === "pending_manual_review"
              ? "Pending Manual Review"
              : transaction.status
            : status,
        )}
        status={
          transaction.status === "pending" ||
          transaction.status === "pending_manual_review"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
      {(transaction.status === "pending_manual_review" ||
        isRiskDispatchPending(transaction)) && (
        <RiskReviewActions transaction={transaction} refetch={refetch} />
      )}
    </div>
  );
};

const RiskReviewActions: React.FC<{
  transaction: Itransaction;
  refetch?: () => void;
}> = ({ transaction, refetch }) => {
  const [confirmModal, setConfirmModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);

  const handleApprove = () => {
    post(`admin/transactions/riskReview/${transaction.id}/approve`, {})
      .then(({ data }) => {
        if (data.success) {
          refetch?.();
          showSuccess(
            isRiskDispatchPending(transaction)
              ? "Processing queued"
              : "Transaction approved",
          );
          setConfirmModal(false);
        }
      })
      .catch(() => {
        showDanger("Something went wrong");
        setConfirmModal(false);
      });
  };

  const handleReject = () => {
    post(`admin/transactions/riskReview/${transaction.id}/reject`, {})
      .then(({ data }) => {
        if (data.success) {
          refetch?.();
          showSuccess("Transaction rejected");
          setCancelModal(false);
        }
      })
      .catch(() => {
        showDanger("Something went wrong");
        setCancelModal(false);
      });
  };

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick
        onRequestClose={() => {
          setConfirmModal(false);
          setCancelModal(false);
        }}
        isOpen={confirmModal || cancelModal}
        className="w-screen h-screen"
      >
        <div
          onClick={() => {
            setConfirmModal(false);
            setCancelModal(false);
          }}
          className="relative w-full h-full bg-gray-500 bg-opacity-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-0 flex flex-col items-center justify-center w-full px-5 bg-white rounded-t-lg py-28"
          >
            <div className="absolute w-20 h-1 bg-white rounded-lg -top-5" />
            <h4 className="mb-5 text-xl">
              {confirmModal
                ? isRiskDispatchPending(transaction)
                  ? "Retry processing for"
                  : "Approve"
                : "Reject"}{" "}
              this transaction?
            </h4>
            <div className="flex mt-10 text-center">
              <AdminPermission permission={"transactions.riskReview.approve"}>
                <VButton
                  onClick={confirmModal ? handleApprove : handleReject}
                  className="mr-20 text-white bg-green-500"
                >
                  Yes
                </VButton>
              </AdminPermission>
              <AdminPermission permission={"transactions.riskReview.approve"}>
                <VButton
                  onClick={() =>
                    confirmModal
                      ? setConfirmModal(false)
                      : setCancelModal(false)
                  }
                  className="text-white"
                >
                  No
                </VButton>
              </AdminPermission>
            </div>
          </div>
        </div>
      </ReactModal>
      <div className="flex items-center justify-center mt-10 gap-x-5 gap-y-5">
        <AdminPermission permission={"transactions.riskReview.approve"}>
          <VButton
            onClick={() => setConfirmModal(true)}
            className="text-white bg-green-600"
          >
            {isRiskDispatchPending(transaction)
              ? "Retry processing"
              : "Approve"}
          </VButton>
        </AdminPermission>
        {!isRiskDispatchPending(transaction) && (
          <AdminPermission permission={"transactions.riskReview.approve"}>
            <VButton
              onClick={() => setCancelModal(true)}
              className="text-white"
            >
              Reject
            </VButton>
          </AdminPermission>
        )}
      </div>
    </>
  );
};

export const Send = ({
  transaction,
  full,
  refetch,
}: {
  transaction: Itransaction;
  full?: boolean;
  refetch?: () => void;
}) => {
  const rate = safeJsonParse(transaction.rate, {} as Record<string, any>);
  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h4 className="mb-5">Sent {transaction.crypto.toUpperCase()}</h4>
      <TransactionDevice device={transaction.device} />
      <TransactionItem
        title={`Amount`}
        value={`${transaction.amount} ${transaction.crypto.toUpperCase()}`}
        isAmount
        sub={
          <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
            <span className="mr-4">
              {`N${moneyFormat(
                Number(transaction.amount) *
                  rate[transaction.crypto] *
                  rate.sell.usd,
              )}`}
            </span>
            <span>
              {`$${moneyFormat(
                Number(transaction.amount) * rate[transaction.crypto],
              )}
              `}
            </span>
          </p>
        }
      />
      {transaction.place === "external" && (
        <>
          <TransactionItem
            title="#Hash"
            value={transaction.transactionHash || ""}
          />
        </>
      )}
      {transaction.place === "external" && (
        <TransactionItem title="External Address" value={transaction.address} />
      )}
      {rate?.isInternal && (
        <TransactionItem
          title="Recipient Address"
          value={transaction.address}
        />
      )}
      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />
      <TransactionItem
        title="Transaction Fee"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.fee)}`}
      />
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />
      {/* //// Receiver */}
      {transaction.place === "bitoshi" && (
        <TransactionItem
          title="Recipient username"
          underline
          value={`@${
            rate?.isInternal ? rate?.receiver : transaction?.receiver?.userName
          }`}
        />
      )}
      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />
      {transaction.riskFlagReason && (
        <TransactionItem
          title="Risk Flag Reason"
          value={transaction.riskFlagReason}
        />
      )}
      <TransactionItem
        title="Status"
        isStatus
        value={
          transaction.status === "pending_manual_review"
            ? "Pending Manual Review"
            : transaction.status
        }
        status={
          transaction.status === "pending" ||
          transaction.status === "pending_manual_review"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
      {(transaction.status === "pending_manual_review" ||
        isRiskDispatchPending(transaction)) && (
        <RiskReviewActions transaction={transaction} refetch={refetch} />
      )}
    </div>
  );
};

export const Receive = ({
  transaction,
  full,
  refetch,
}: {
  transaction: Itransaction;
  full?: boolean;
  refetch?: () => void;
}) => {
  const rate = safeJsonParse(transaction.rate, {} as Record<string, any>);
  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h4 className="mb-5">Received {transaction.crypto.toUpperCase()}</h4>
      <TransactionDevice device={transaction.device} />
      <TransactionItem
        title={`Amount`}
        value={`${transaction.amount} ${transaction.crypto.toUpperCase()}`}
        isAmount
        sub={
          <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
            <span className="mr-4">
              {`N${moneyFormat(
                Number(transaction.amount) *
                  rate[transaction.crypto] *
                  rate.sell.usd,
              )}`}
            </span>
            <span>
              {`$${moneyFormat(
                Number(transaction.amount) * rate[transaction.crypto],
              )}
              `}
            </span>
          </p>
        }
      />
      <TransactionItem
        title="Network"
        value={(transaction.fromCrypto || transaction.crypto).toUpperCase()}
      />
      {transaction.place === "external" && (
        <TransactionItem
          title="#Hash"
          value={transaction.transactionHash || ""}
        />
      )}
      <TransactionItem title="Receiving Address" value={transaction.address} />
      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />

      {transaction.place === "bitoshi" && (
        <TransactionItem
          title="Sender username"
          underline
          value={`@${transaction?.receiver?.userName || rate?.sender}`}
        />
      )}

      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />
      {transaction.riskFlagReason && (
        <TransactionItem
          title="Risk Flag Reason"
          value={transaction.riskFlagReason}
        />
      )}
      <TransactionItem
        title="Status"
        isStatus
        value={
          transaction.status === "pending_manual_review"
            ? "Pending Manual Review"
            : transaction.status
        }
        status={
          transaction.status === "pending" ||
          transaction.status === "pending_manual_review"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
      {(transaction.status === "pending_manual_review" ||
        isRiskDispatchPending(transaction)) && (
        <RiskReviewActions transaction={transaction} refetch={refetch} />
      )}
    </div>
  );
};

export const Buy = ({
  transaction,
  full,
  refetch,
}: {
  transaction: Itransaction;
  full?: boolean;
  refetch?: () => void;
}) => {
  const rate = safeJsonParse(transaction.rate, {} as Record<string, any>);
  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h4 className="mb-5">Bought {transaction.crypto.toUpperCase()}</h4>
      <TransactionDevice device={transaction.device} />
      <TransactionItem
        title={`Amount`}
        value={`${transaction.amount} ${transaction.crypto.toUpperCase()}`}
        isAmount
        sub={
          <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
            <span className="mr-4">
              {`N${moneyFormat(
                Number(transaction.amount) *
                  rate[transaction.crypto] *
                  rate.buy.usd,
              )}`}
            </span>
            <span>
              {`$${moneyFormat(
                Number(transaction.amount) * rate[transaction.crypto],
              )}
              `}
            </span>
          </p>
        }
      />
      {transaction.place === "external" && (
        <>
          <TransactionItem
            title="#Hash"
            value={transaction.transactionHash || ""}
          />
        </>
      )}
      <TransactionItem title="Rate" value={`N${rate?.buy?.usd || ""}/$`} />
      <TransactionItem
        title="Price"
        value={`N${
          moneyFormat(rate?.buy?.usd * rate?.[transaction.crypto]) || ""
        }/${transaction.crypto?.toUpperCase()}`}
      />
      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />
      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />
      {transaction.place === "external" && (
        <TransactionItem title="External Address" value={transaction.address} />
      )}
      {rate?.isInternal && (
        <TransactionItem
          title="Recipient User"
          underline
          value={`@${rate?.receiver}`}
        />
      )}
      {rate?.isInternal && (
        <TransactionItem
          title="Recipient Address"
          value={transaction.address}
        />
      )}
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />
      {transaction.place === "external" && (
        <TransactionItem
          title="Fee"
          value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.fee)}`}
        />
      )}
      {transaction.riskFlagReason && (
        <TransactionItem
          title="Risk Flag Reason"
          value={transaction.riskFlagReason}
        />
      )}
      <TransactionItem
        title="Status"
        isStatus
        value={
          transaction.status === "pending_manual_review"
            ? "Pending Manual Review"
            : transaction.status
        }
        status={
          transaction.status === "pending" ||
          transaction.status === "pending_manual_review"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
      {(transaction.status === "pending_manual_review" ||
        isRiskDispatchPending(transaction)) && (
        <RiskReviewActions transaction={transaction} refetch={refetch} />
      )}
    </div>
  );
};

export const Sell = ({
  transaction,
  full,
  refetch,
}: {
  transaction: Itransaction;
  full?: boolean;
  refetch?: () => void;
}) => {
  const rate = safeJsonParse(transaction.rate, {} as Record<string, any>);
  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h4 className="mb-5">Sold {transaction.crypto.toUpperCase()}</h4>
      <TransactionDevice device={transaction.device} />
      <TransactionItem
        title={`Amount`}
        value={`${transaction.amount} ${transaction.crypto.toUpperCase()}`}
        isAmount
        sub={
          <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
            <span className="mr-4">
              {`N${moneyFormat(
                Number(transaction.amount) *
                  rate[transaction.crypto] *
                  rate.sell.usd,
              )}`}
            </span>
            <span>
              {`$${moneyFormat(
                Number(transaction.amount) * rate[transaction.crypto],
              )}
              `}
            </span>
          </p>
        }
      />
      {transaction.place === "external" && (
        <>
          <TransactionItem
            title="#Hash"
            value={transaction.transactionHash || ""}
          />
          <TransactionItem
            title="Receiving Address"
            value={transaction.address}
          />
        </>
      )}
      <TransactionItem title="Rate" value={`N${rate?.sell?.usd || ""}/$`} />
      <TransactionItem
        title="Price"
        value={`N${
          moneyFormat(rate?.sell?.usd * rate?.[transaction.crypto]) || ""
        }/${transaction.crypto?.toUpperCase()}`}
      />
      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />
      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      {transaction.place === "bitoshi" && rate?.isInternal && (
        <TransactionItem title="Sender" underline value={`@${rate?.sender}`} />
      )}

      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />
      {transaction.place === "external" && (
        <TransactionItem title="External Address" value={transaction.address} />
      )}
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />
      {transaction.place === "external" && (
        <TransactionItem
          title="Fee"
          value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.fee)}`}
        />
      )}

      <TransactionItem
        title="Status"
        isStatus
        value={
          transaction.status === "pending_manual_review"
            ? "Pending Manual Review"
            : transaction.status
        }
        status={
          transaction.status === "pending" ||
          transaction.status === "pending_manual_review"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
      {transaction.riskFlagReason && (
        <TransactionItem
          title="Risk Flag Reason"
          value={transaction.riskFlagReason}
        />
      )}
      {(transaction.status === "pending_manual_review" ||
        isRiskDispatchPending(transaction)) && (
        <RiskReviewActions transaction={transaction} refetch={refetch} />
      )}
    </div>
  );
};

export const Bill = ({
  transaction,
  full,
  refetch,
}: {
  transaction: Itransaction;
  full?: boolean;
  refetch?: () => void;
}) => {
  let bank =
    transaction?.address && transaction?.address !== "sell.bitoshi.africa"
      ? safeJsonParse(transaction.address, {} as Record<string, any>)
      : {};

  const isCrypto = transaction.currencyType === "crypto";
  const isMobile = ["airtime", "mobile_data"].includes(transaction.fromCrypto);
  const isCable = transaction.fromCrypto === "cable";
  const isElectricity = transaction.fromCrypto === "electricity";
  const isBetting = transaction.fromCrypto === "betting";
  const isData = transaction.fromCrypto === "mobile_data";
  const [confirmModal, setConfirmModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);

  const close = () => {
    setConfirmModal(false);
    setCancelModal(false);
  };

  const handleSubmit = () => {
    post(`admin/transactions/paymentSent/${transaction.id}`, {})
      .then(({ data }) => {
        if (data.success) {
          if (refetch) {
            refetch();
          }
          showSuccess("Payment marked as successful");
          setConfirmModal(false);
        }
      })
      .catch((e) => {
        showDanger("Something went wrong");
        setConfirmModal(false);
      });
  };

  const handleSubmitForFailed = () => {
    post(`admin/transactions/paymentFailed/${transaction.id}`, {})
      .then(({ data }) => {
        if (data.success) {
          if (refetch) {
            refetch();
          }
          showSuccess("Payment cancelled successfully");
          setCancelModal(false);
        }
      })
      .catch((e) => {
        showDanger("Something went wrong");
        setCancelModal(false);
      });
  };

  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ReactModal
        shouldCloseOnOverlayClick={true}
        onRequestClose={close}
        isOpen={confirmModal || cancelModal}
        className="w-screen h-screen"
      >
        <div
          onClick={close}
          className="relative w-full h-full bg-gray-500 bg-opacity-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-0 flex flex-col items-center justify-center w-full px-5 bg-white rounded-t-lg py-28"
          >
            <div className="absolute w-20 h-1 bg-white rounded-lg -top-5"></div>
            <h4 className="mb-5 text-xl">
              {billsTitle[transaction.fromCrypto]}{" "}
              {confirmModal ? "completed" : "failed"}?
            </h4>

            <div className="flex mt-10 text-center">
              <AdminPermission
                permission={"transactions.paymentReconciliation.approve"}
              >
                <VButton
                  onClick={confirmModal ? handleSubmit : handleSubmitForFailed}
                  className="mr-20 text-white bg-green-500"
                >
                  Yes
                </VButton>
              </AdminPermission>
              <AdminPermission
                permission={"transactions.paymentReconciliation.approve"}
              >
                <VButton
                  onClick={
                    confirmModal
                      ? () => setConfirmModal(false)
                      : () => setCancelModal(false)
                  }
                  className="text-white"
                >
                  No
                </VButton>
              </AdminPermission>
            </div>
          </div>
        </div>
      </ReactModal>

      <h4 className="mb-5">{billsTitle[transaction.fromCrypto]}</h4>
      <TransactionDevice device={transaction.device} />
      <TransactionItem
        title="Amount"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(
          Number(transaction.amount) - Number(transaction.fee),
        )}`}
        isAmount
        sub={
          isCrypto ? (
            <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
              <span className="mr-4">
                {`${cryptoFormat(
                  Number(transaction.amount) /
                    bank.usd /
                    bank[transaction?.crypto],
                )} ${transaction?.crypto?.toUpperCase()}`}
              </span>
              <span>
                {`$${moneyFormat(Number(transaction.amount) / bank.usd)}
              `}
              </span>
            </p>
          ) : undefined
        }
      />
      <TransactionItem
        title="Fee"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.fee)}`}
      />
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />
      <TransactionItem
        title="Service Provider"
        value={capitalize(bank?.provider ?? "")}
      />
      <TransactionItem
        title="Network"
        value={transaction?.network?.toUpperCase()}
      />
      {isMobile && (
        <TransactionItem title="Phone Number" value={bank.phoneNumber} />
      )}
      {isCable && (
        <TransactionItem title="Smart Card Number" value={bank.iucNumber} />
      )}
      {isElectricity && (
        <TransactionItem title="Meter Number" value={bank.meterNumber} />
      )}
      {isBetting && (
        <TransactionItem title="Customer ID" value={bank.customerID} />
      )}
      {isData && <TransactionItem title="Plan" value={bank.plan} />}

      {(isElectricity || isCable || isBetting) && (
        <TransactionItem
          title="Package"
          value={
            isElectricity
              ? capitalize(bank.meterType)
              : capitalize(bank.package)
          }
        />
      )}
      {isElectricity && (
        <TransactionItem title="Meter Token" value={bank.metertoken} />
      )}

      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />

      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />

      <TransactionItem
        title="Source"
        value={
          isCrypto
            ? transaction?.crypto?.toUpperCase()
            : capitalize(transaction?.currencyType)
        }
      />

      {isCrypto && (
        <TransactionItem
          title="Rate"
          value={`1${transaction?.crypto?.toUpperCase()} = $${
            bank[transaction?.crypto]
          }`}
          sub={
            <p className="text-sm font-light text-black text-opacity-70">
              $1 = {bank?.usd}
            </p>
          }
        />
      )}

      {transaction.riskFlagReason && (
        <TransactionItem
          title="Risk Flag Reason"
          value={transaction.riskFlagReason}
        />
      )}
      <TransactionItem
        title="Status"
        isStatus
        value={
          transaction.status === "pending_manual_review"
            ? "Pending Manual Review"
            : transaction.status
        }
        status={
          transaction.status === "pending" ||
          transaction.status === "pending_manual_review"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
      {(transaction.status === "pending_manual_review" ||
        isRiskDispatchPending(transaction)) && (
        <RiskReviewActions transaction={transaction} refetch={refetch} />
      )}
      {transaction.status === "pending" &&
        !isRiskDispatchPending(transaction) && (
          <div className="flex items-center justify-center mt-10 gap-x-5 gap-y-5">
            <AdminPermission
              permission={"transactions.paymentReconciliation.approve"}
            >
              <VButton
                onClick={() => setConfirmModal((p) => !p)}
                className="text-white"
              >
                Payment Sent
              </VButton>
            </AdminPermission>
            <AdminPermission
              permission={"transactions.paymentReconciliation.approve"}
            >
              <VButton
                onClick={() => setCancelModal((p) => !p)}
                className="text-white"
              >
                Payment Cancelled
              </VButton>
            </AdminPermission>
          </div>
        )}
    </div>
  );
};

export const Refund = ({
  transaction,
  full,
}: {
  transaction: Itransaction;
  full?: boolean;
  refetch?: () => void;
}) => {
  let bank =
    transaction?.address && transaction?.address !== "sell.bitoshi.africa"
      ? safeJsonParse(transaction.address, {} as Record<string, any>)
      : {};

  const isCrypto = transaction.currencyType === "crypto";

  return (
    <div
      className={[
        full ? "" : "w-5/12 md:w-4/12",
        "bg-orange-50 rounded-lg py-10 px-8 md:fixed md:top-28 md:max-h-[81vh] md:overflow-y-auto md:right-5",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {transaction.place === "external" ? (
        <span className="px-1 text-xs bg-orange-300 rounded">
          sell.bitoshi.africa
        </span>
      ) : (
        ""
      )}
      <h4 className="mb-5">Refund</h4>
      <TransactionDevice device={transaction.device} />
      <TransactionItem
        title="Amount"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(
          Number(transaction.amount) - Number(transaction.fee),
        )}`}
        isAmount
        sub={
          isCrypto ? (
            <p className="mt-1 mb-3 text-sm font-light text-opacity-50">
              <span className="mr-4">
                {`${cryptoFormat(
                  Number(transaction.amount) /
                    bank.usd /
                    bank[transaction?.fromCrypto],
                )} ${transaction?.fromCrypto?.toUpperCase()}`}
              </span>
              <span>
                {`$${moneyFormat(Number(transaction.amount) / bank.usd)}
              `}
              </span>
            </p>
          ) : undefined
        }
      />
      <TransactionItem
        title="Fee"
        value={`${currency(transaction.currency || "Fiat")} ${moneyFormat(transaction.fee)}`}
      />
      <TransactionItem
        title="Transaction ID"
        value={transaction.transactionID}
      />
      <TransactionItem title="Bank name" value={bank?.bankName} />
      <TransactionItem title="Account Number" value={bank?.accountNumber} />
      <TransactionItem title="Account name" value={bank?.accountName} />
      <TransactionItem
        title="User"
        underline
        value={`@${transaction?.user?.userName}`}
      />
      <TransactionItem
        title="Registered full name"
        value={`${transaction?.user?.firstName} ${transaction?.user?.lastName}`}
      />
      <TransactionItem
        title={
          transaction?.paymentProviderName ? "Payment provider" : "Merchant"
        }
        underline
        value={
          transaction?.paymentProviderName || `@${transaction?.merchant || ""}`
        }
      />
      <TransactionItem
        title="Date & Time"
        value={`${DateTime.fromISO(transaction.timestamp).toFormat(
          "DD",
        )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`}
      />

      <TransactionItem
        title="Source"
        value={
          isCrypto
            ? transaction?.fromCrypto?.toUpperCase()
            : capitalize(transaction?.currencyType)
        }
      />

      {isCrypto && (
        <TransactionItem
          title="Rate"
          value={`1${transaction?.fromCrypto?.toUpperCase()} = $${
            bank[transaction?.fromCrypto]
          }`}
          sub={
            <p className="text-sm font-light text-black text-opacity-70">
              $1 = {bank?.usd}
            </p>
          }
        />
      )}

      <TransactionItem
        title="Status"
        isStatus
        value={transaction.status}
        status={
          transaction.status === "pending"
            ? "text-orange-500 capitalize"
            : transaction.status === "successful"
              ? "text-green-500 capitalize"
              : "text-red-500 capitalize"
        }
      />
    </div>
  );
};

export default TransactionInfo;
